'use client';

import { useEffect, useMemo, useRef } from 'react';
import { useReactFlow, useStore } from '@xyflow/react';

import { createLogger } from '@core/be/platform/logging/Logger';

type CanvasPoint = [number, number];

type NodePointLookup = Record<string, CanvasPoint[]>;
type NodeRawIdLookup = Record<string, string>;

export interface MindmapLassoProps {
  partial?: boolean;
  path: CanvasPoint[];
  isActive: boolean;
  onSelectionChange?: (rawIds: string[]) => void;
  origin?: { left: number; top: number } | null;
}

export function MindmapLasso({ partial = false, path, isActive, onSelectionChange, origin }: MindmapLassoProps) {
  const { flowToScreenPosition } = useReactFlow();
  const { width, height, nodeLookup } = useStore((state) => ({
    width: state.width,
    height: state.height,
    nodeLookup: state.nodeLookup,
  }));
  const prevSelectedRef = useRef<string[]>([]);

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const ctxRef = useRef<CanvasRenderingContext2D | null>(null);
  const loggerRef = useRef(createLogger('mindmap:lasso'));
  const logger = loggerRef.current;

  const toRawId = (id: string) => (id.includes('::') ? id.split('::').pop() ?? id : id);
  const nodeRawIds = useMemo<NodeRawIdLookup>(() => {
    const lookup: NodeRawIdLookup = {};
    nodeLookup.forEach((node, nodeId) => {
      const fromData = (node.data as { id?: string } | undefined)?.id;
      const rawId = fromData ? toRawId(fromData) : toRawId(nodeId);
      lookup[nodeId] = rawId;
    });
    return lookup;
  }, [nodeLookup]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = ctxRef.current ?? canvas.getContext('2d');
    if (!ctx) return;
    ctxRef.current = ctx;

    ctx.clearRect(0, 0, width, height);

    if (!isActive || path.length < 2) {
      return;
    }

    const lassoPath = new Path2D();
    const [startX, startY] = path[0];
    lassoPath.moveTo(startX, startY);
    for (let i = 1; i < path.length; i += 1) {
      const [px, py] = path[i];
      lassoPath.lineTo(px, py);
    }
    lassoPath.closePath();

    ctx.lineWidth = 1;
    ctx.fillStyle = 'rgba(0, 89, 220, 0.08)';
    ctx.strokeStyle = 'rgba(0, 89, 220, 0.8)';
    ctx.fill(lassoPath);
    ctx.stroke(lassoPath);

    // Use the same origin the lasso path used (pane rect), fallback to canvas rect
    const rect = origin ?? canvas.getBoundingClientRect();
    const selectedInternalIds = new Set<string>();
    const selectedRawIds: string[] = [];

    const toCanvas = (screenX: number, screenY: number) => ({ x: screenX - rect.left, y: screenY - rect.top });

    const flowRoot = canvas.closest('.react-flow') as HTMLElement | null;

    nodeLookup.forEach((_node, nodeId) => {
      let corners: CanvasPoint[] | null = null;
      if (flowRoot) {
        const el = flowRoot.querySelector(`.react-flow__node[data-id="${CSS.escape(nodeId)}"]`) as HTMLElement | null;
        if (el) {
          const r = el.getBoundingClientRect();
          corners = [
            [r.left, r.top],
            [r.right, r.top],
            [r.right, r.bottom],
            [r.left, r.bottom],
          ].map(([sx, sy]) => {
            const { x, y } = toCanvas(sx, sy);
            return [x, y];
          }) as CanvasPoint[];
        }
      }

      if (!corners) {
        // Fallback to flow coords if DOM node not found
        const node = nodeLookup.get(nodeId);
        if (node) {
          const { x, y } = node.internals.positionAbsolute;
          const w = node.measured?.width ?? 0;
          const h = node.measured?.height ?? 0;
          corners = [
            [x, y],
            [x + w, y],
            [x + w, y + h],
            [x, y + h],
          ].map(([fx, fy]) => {
            const sp = flowToScreenPosition({ x: fx, y: fy });
            const { x, y } = toCanvas(sp.x, sp.y);
            return [x, y];
          }) as CanvasPoint[];
        }
      }

      if (!corners) return;

      const hits = corners.map(([cx, cy]) => ctx.isPointInPath(lassoPath, cx, cy));
      const matches = partial ? hits.some(Boolean) : hits.every(Boolean);
      if (matches) {
        selectedInternalIds.add(nodeId);
        selectedRawIds.push(nodeRawIds[nodeId] ?? toRawId(nodeId));
      }
    });

    const prev = prevSelectedRef.current;
    const next = Array.from(selectedInternalIds).sort();
    const same = prev.length === next.length && prev.every((v, i) => v === next[i]);
    if (!same) {
      prevSelectedRef.current = next;
      onSelectionChange?.(selectedRawIds);
      logger.debug('lasso selection updated: %o', selectedRawIds);
    }
  }, [flowToScreenPosition, height, isActive, nodeLookup, nodeRawIds, onSelectionChange, origin, partial, path, width, logger]);

  return <canvas ref={canvasRef} width={width} height={height} className='mindmap-lasso-overlay' />;
}

export default MindmapLasso;
